import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { finalize } from 'rxjs';

import { AuthSessionService } from '../../services/auth-session.service';
import { GlobalFeedbackService } from '../../services/global-feedback.service';

@Component({
  selector: 'app-shell',
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss',
})
export class AppShellComponent {
  readonly authSession = inject(AuthSessionService);

  loggingOut = false;
  quickAccessOpen = false;
  documentsNavOpen = false;
  apiNavOpen = false;
  settingsNavOpen = false;

  getGreeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  }

  getUserInitials(): string {
    const user = this.authSession.currentUser();
    if (!user || !user.name) return 'U';
    const parts = user.name.trim().split(/\s+/);
    if (parts.length === 0) return 'U';
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }


  private readonly router = inject(Router);
  private readonly feedback = inject(GlobalFeedbackService);
  private readonly cdr = inject(ChangeDetectorRef);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('[data-quick-access]')) {
      return;
    }

    this.quickAccessOpen = false;
  }

  toggleQuickAccess(): void {
    this.quickAccessOpen = !this.quickAccessOpen;
  }

  closeQuickAccess(): void {
    this.quickAccessOpen = false;
  }

  toggleDocumentsNav(): void {
    this.documentsNavOpen = !this.documentsNavOpen;
  }

  toggleApiNav(): void {
    this.apiNavOpen = !this.apiNavOpen;
  }

  toggleSettingsNav(): void {
    this.settingsNavOpen = !this.settingsNavOpen;
  }

  isDocumentsRoute(): boolean {
    return this.router.url.startsWith('/documents');
  }

  isDocumentsNavExpanded(): boolean {
    return this.isDocumentsRoute() || this.documentsNavOpen;
  }

  isApiRoute(): boolean {
    return this.router.url.startsWith('/api-management');
  }

  isApiNavExpanded(): boolean {
    return this.isApiRoute() || this.apiNavOpen;
  }

  isSettingsRoute(): boolean {
    return this.router.url.startsWith('/settings');
  }

  isSettingsNavExpanded(): boolean {
    return this.isSettingsRoute() || this.settingsNavOpen;
  }

  logout(): void {
    if (this.loggingOut) {
      return;
    }

    this.loggingOut = true;

    this.authSession
      .logout()
      .pipe(
        finalize(() => {
          this.loggingOut = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.feedback.clearError();
          void this.router.navigate(['/login']);
        },
        error: () => {
          void this.router.navigate(['/login']);
        },
      });
  }
}
